/**
 * Range bounds of a number filter, between what the user types and what the filter stores.
 *
 *   typed "100"  ──toStoredBound──►  10000   (a column whose transform stores cents)
 *   typed ""     ──toStoredBound──►  null    (no bound — never sent through the transform)
 *   stored 0     ──findRangeBound─►  0       (looked up by presence, so 0 is a bound)
 */
import type { FilterColumn, FilterItemProps } from './filterItemTypes';

type RangeBound = 'gte' | 'lte';

/**
 * The stored bound, or `undefined` when the filter has none. Looked up by presence: a
 * truthiness test (`where.find((w) => w.gte)`) drops a bound of 0.
 */
export function findRangeBound(where: FilterItemProps['filter']['where'] | undefined, bound: RangeBound) {
  return where?.find((condition) => condition[bound] !== undefined)?.[bound];
}

/** A stored bound in the units the user types (dollars for a column that stores cents). */
export function toTypedBound(column: FilterColumn, stored: unknown): unknown {
  if (stored === undefined || stored === null) return stored;
  return column.serverFilterValueTransform?.from?.(stored) ?? stored;
}

/** The typed text as a stored bound; blank or non-finite text is NO bound, never a converted `null`. */
export function toStoredBound(column: FilterColumn, typed: string): number | null {
  if (typed.trim() === '') return null;
  const value = Number(typed);
  if (!Number.isFinite(value)) return null;
  // Only a real number reaches the transform: `Money.toCents(null)` is 0, a bound nobody typed.
  const stored = column.serverFilterValueTransform?.to?.(value) ?? value;
  return typeof stored === 'number' && Number.isFinite(stored) ? stored : null;
}

/** The input text for a stored bound; empty when the filter has none. */
export const typedBoundText = (column: FilterColumn, stored: unknown): string => {
  const typed = toTypedBound(column, stored);
  return typed === undefined || typed === null ? '' : String(typed);
};
